import React from 'react'
import AdminBillingAccountDetailContext from './context'
import { Alert, Box, Button, InputAdornment, Typography } from '@mui/material'
import CurrencyPoundIcon from '@mui/icons-material/CurrencyPound'
import SwitchField from 'components/SwitchField'
import { AdminBillingAccountUpdateForm } from 'types/interfaces'
import AdminBillingAccountUpdateFormError from 'errors/AdminBillingAccountUpdateFormError'
import update from 'immutability-helper'
import { useAPI } from 'contexts/APIProvider'
import { useSnackBarAlert } from 'contexts/SnackBarAlertProvider'
import FormField from 'components/FormField'
import { fromCentsToPounds, fromPountsToCents } from 'logic/helpers'
import Invoice from 'logic/Invoice'
import {
    DataGridPro,
    GridColDef,
    GridRenderCellParams,
    GridValueGetterParams,
} from '@mui/x-data-grid-pro';

const AdminBillingAccountDetailLateInvoices: React.FC = () => {
    const { api } = useAPI()
    const { showAlert } = useSnackBarAlert()
    const {
        billingAccount,
        fetchBillingAccount,
    } = React.useContext(AdminBillingAccountDetailContext)

    const [invoices, setInvoices] = React.useState<Invoice[]>([])
    const [form, setForm] = React.useState<AdminBillingAccountUpdateForm>({})
    const [error, setError] = React.useState<AdminBillingAccountUpdateFormError>()
    const [saving, setSaving] = React.useState<boolean>(false)
    const [loading, setLoading] = React.useState<boolean>(false)

    const updateForm = React.useCallback((name: string, value: any) => {
        setForm(update(form, { [name]: { $set: value } }))
    }, [form])

    const save = React.useCallback(async () => {
        if (!billingAccount) return
        try {
            setSaving(true)
            await api.updateBillingAccountByAdmin(billingAccount.id, form)
            await fetchBillingAccount()
            showAlert('success', 'Billing account updated successfully')
        } catch (error) {
            if (error instanceof AdminBillingAccountUpdateFormError) {
                setError(error)
            } else {
                console.error(error)
            }
        } finally {
            setSaving(false)
        }
    }, [api, billingAccount, fetchBillingAccount, form, showAlert])

    const fetchInvoices = React.useCallback(async () => {
        if (!billingAccount) return
        try {
            setLoading(true)
            setInvoices(await api.listInvoices({ billing_account_id: billingAccount.id, status: 'open', is_job_payments: true }))
        } catch (error) {
            console.error(error)
        } finally {
            setLoading(false)
        }
    }, [api, billingAccount])

    const columns: GridColDef<Invoice>[] = React.useMemo(() => ([{
        minWidth: 150,
        headerName: 'Invoice',
        field: 'invoice_number',
        type: 'string',
    }, {
        minWidth: 100,
        headerName: 'Amount',
        field: 'amount_due',
        type: 'number',
        renderCell: (params: GridRenderCellParams<Invoice>) => {
            const record = params.row
            return record.amount_label
        }
    }, {
        minWidth: 150,
        headerName: 'Sent At',
        field: 'finalized_at',
        type: 'dateTime',
        valueGetter: (params: GridValueGetterParams<Invoice>) => {
            return params.row.finalized_at?.toJSDate()
        },
        renderCell: (params: GridRenderCellParams<Invoice>) => {
            const record = params.row
            return record.finalized_at_label()
        }
    }, {
        minWidth: 150,
        headerName: 'Late Fees Start At',
        field: 'late_cycle_start_date',
        type: 'dateTime',
        valueGetter: (params: GridValueGetterParams<Invoice>) => {
            return params.row.late_cycle_start_date?.toJSDate()
        },
        renderCell: (params: GridRenderCellParams<Invoice>) => {
            const record = params.row
            return record.late_cycle_start_date_label()
        }
    }, {
        minWidth: 150,
        headerName: 'Days late',
        field: 'late_days',
        type: 'number',
        renderCell: (params: GridRenderCellParams<Invoice>) => {
            const record = params.row
            return `${record.late_days} days`
        }
    }, {
        minWidth: 150,
        headerName: 'Next Late Fee Date',
        field: 'next_late_cycle_date',
        type: 'dateTime',
        valueGetter: (params: GridValueGetterParams<Invoice>) => {
            return params.row.next_late_cycle_date?.toJSDate()
        },
        renderCell: (params: GridRenderCellParams<Invoice>) => {
            const record = params.row
            return record.next_late_cycle_date_label()
        }
    }, {
        minWidth: 150,
        headerName: 'Next Late Fee',
        field: 'late_amount_per_cycle',
        type: 'number',
        renderCell: (params: GridRenderCellParams<Invoice>) => {
            const record = params.row
            return `£${fromCentsToPounds(record.late_amount_per_cycle)}`
        }
    }, {
        minWidth: 300,
        headerName: 'Cumulative Late Fee',
        field: 'late_amount_cumulative',
        type: 'number',
        renderCell: (params: GridRenderCellParams<Invoice>) => {
            const record = params.row
            return `£${fromCentsToPounds(record.late_amount_cumulative)} (£${fromCentsToPounds(record.late_amount_invoiced)} invoiced) over ${record.late_cycles} weeks`
        }
    }]), [])

    const totalLateAmountCumulative = React.useMemo(() => {
        const total = invoices.reduce((acc, record) => acc + record.late_amount_cumulative, 0)
        return `£${fromCentsToPounds(total)}`
    }, [invoices])

    React.useEffect(() => {
        if (billingAccount) {
            setForm(update(form, {
                invoice_late_fee_start_after: { $set: billingAccount.invoice_late_fee_start_after.as('days') },
                invoice_late_fee_per_job: { $set: billingAccount.invoice_late_fee_per_job },  // in cents
                should_send_invoice_to_practice_managers: { $set: billingAccount.should_send_invoice_to_practice_managers },
                should_send_invoice_summary_to_billing_team: { $set: billingAccount.should_send_invoice_summary_to_billing_team },
                should_include_late_fees_in_invoice_summary: { $set: billingAccount.should_include_late_fees_in_invoice_summary },
            }))
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [billingAccount])

    React.useEffect(() => {
        fetchInvoices()
    }, [fetchInvoices])

    return (
        <Box sx={{ display: 'flex', gap: 1 }}>
            <Box sx={{ flexGrow: 1, display: 'flex', flexDirection: 'column', gap: 2, padding: 1 }}>
                <SwitchField
                    value={form.should_send_invoice_to_practice_managers}
                    label='Send Invoice to Practice Managers'
                    onChange={(value) => updateForm('should_send_invoice_to_practice_managers', value)}
                />
                <SwitchField
                    value={form.should_send_invoice_summary_to_billing_team}
                    label='Send Invoice Summary to Billing Team'
                    onChange={(value) => updateForm('should_send_invoice_summary_to_billing_team', value)}
                />
                <SwitchField
                    value={form.should_include_late_fees_in_invoice_summary}
                    label='Include Late Fee threats in invoice summary'
                    onChange={(value) => updateForm('should_include_late_fees_in_invoice_summary', value)}
                />
                <FormField
                    name='invoice_late_fee_start_after'
                    label='How many days (after invoice is sent) shall we start accruing late fees?'
                    type='number'
                    value={form.invoice_late_fee_start_after ?? 7}
                    onChange={(value) => updateForm('invoice_late_fee_start_after', value ? Number(value) : undefined)}
                    errors={error?.invoice_late_fee_start_after ?? []}
                />
                <FormField
                    name='invoice_late_fee_per_job'
                    label='How much shall we charge per job?'
                    type='number'
                    value={fromCentsToPounds(form.invoice_late_fee_per_job ?? 0)}
                    onChange={(e) => updateForm('invoice_late_fee_per_job', fromPountsToCents(e.target.value))}
                    errors={error?.invoice_late_fee_per_job ?? []}
                    InputProps={{
                        startAdornment: (
                            <InputAdornment position="start">
                                <CurrencyPoundIcon />
                            </InputAdornment>
                        ),
                    }}
                />
                <Button variant='contained' onClick={save} disabled={saving}>
                    {saving ? 'Please Wait ...' : 'Save'}
                </Button>
            </Box>
            <Box sx={{ flexGrow: 2, display: 'flex', flexDirection: 'column', gap: 1 }}>
                <Alert severity='info'>Total late fees so far: <strong>{totalLateAmountCumulative}</strong></Alert>
                <DataGridPro 
                    rows={invoices}
                    columns={columns}
                    getRowId={(record) => record.id}
                    loading={loading}
                    hideFooter
                />
            </Box>

        </Box>
    )
}

export default AdminBillingAccountDetailLateInvoices